/**
 * 担当ボードのサービス行に将来日のカバー予定を🎌（オペ担）/🔍（review_sql）バッジで並べる。整形は純関数
 * `upcomingCovers` に委譲する。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 担当ボードのサービス行に将来日のカバー予定を🎌/🔍バッジで表示する presentational コンポーネント
 * @graph-connects none
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { OpsCover } from '../../../types/ops.js';
import { upcomingCovers } from './upcoming-covers.js';

/** UpcomingCoverChips の props。 @graph-connects none */
export interface UpcomingCoverChipsProps {
  /** サービスの現行カバー一覧（過去分は保存時に掃除済み）。 */
  covers: OpsCover[];
  /** 基準日時（今日のカバーは現在セルに出るため除外する）。 */
  now: Date;
}

/**
 * 将来日のカバー予定を🎌（オペ担）/🔍（review_sql）バッジで並べる。予定が無ければ null を返す。
 * @graph-connects none
 */
export function UpcomingCoverChips({ covers, now }: UpcomingCoverChipsProps): ReactNode {
  const { t } = useTranslation();
  const upcoming = upcomingCovers(covers, now);
  if (upcoming.length === 0) return null;
  return (
    <div
      className="mt-1 flex flex-wrap gap-1"
      aria-label={t('ops.assignments.upcomingCovers.ariaLabel')}
    >
      {upcoming.map((c) => {
        const md = `${String(Number(c.date.slice(5, 7)))}/${String(Number(c.date.slice(8, 10)))}`;
        const isOpetan = c.role === 'opetan';
        return (
          <span
            key={`${c.date}-${c.role}`}
            title={t(
              isOpetan
                ? 'ops.assignments.upcomingCovers.opetanTitle'
                : 'ops.assignments.upcomingCovers.reviewerTitle',
              { date: c.date, who: c.who },
            )}
            className="inline-flex items-center gap-0.5 rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-1.5 py-0.5 text-xs font-normal text-[var(--color-text-secondary)]"
          >
            {isOpetan ? '🎌' : '🔍'} {md} → {c.who}
          </span>
        );
      })}
    </div>
  );
}
